import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Input, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LOCATION_STATUS_META } from '@goelectric/ui-components';
import type {
  AdminLocationSummary,
  AllySummary,
  LocationStatus,
  PageMeta,
} from '@goelectric/shared-types';
import { allyDisplayName } from '@/features/aliados/utils/format';
import type { LocationFilters } from '../hooks/useLocations';
import { LocationStatusBadge, LocationSyncBadge } from './LocationBadges';

/** Debounce de la búsqueda antes de golpear el backend (server-side). */
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_OPTIONS: { value: LocationStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Todos los estados' },
  ...(['ACTIVE', 'INACTIVE'] as const).map((value) => ({
    value,
    label: LOCATION_STATUS_META[value].label,
  })),
];

export interface LocationTableProps {
  locations: AdminLocationSummary[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  filters: LocationFilters;
  /** Aliados para el filtro (typeahead) y para resolver el nombre de cada locación. */
  allies: AllySummary[];
  /** Muestra el CTA del empty-state; solo con permiso de escritura. */
  canCreate: boolean;
  onFiltersChange: (filters: LocationFilters) => void;
  onOffsetChange: (offset: number) => void;
  onRetry: () => void;
  onCreate: () => void;
  onSelect: (locationId: string) => void;
}

/** `ACTIVE` es el default del listado: no cuenta como filtro activo del empty-state. */
function hasActiveFilters(filters: LocationFilters): boolean {
  return Boolean(filters.q || filters.allyId || (filters.status && filters.status !== 'ACTIVE'));
}

/**
 * Tabla CRUD de Locaciones (guía de estilo §8): búsqueda, filtros por aliado/estado,
 * paginación server-side (con el `meta` del contrato), empty-state con acción sugerida
 * y badges de estado + sincronización.
 */
export function LocationTable({
  locations,
  meta,
  loading,
  error,
  filters,
  allies,
  canCreate,
  onFiltersChange,
  onOffsetChange,
  onRetry,
  onCreate,
  onSelect,
}: LocationTableProps) {
  const [query, setQuery] = useState(filters.q ?? '');

  // Sincroniza la búsqueda con el backend con debounce (evita una request por tecla).
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = query.trim();
      if ((filters.q ?? '') !== next) {
        onFiltersChange({ ...filters, q: next || undefined });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query, filters, onFiltersChange]);

  const allyOptions = useMemo(
    () => allies.map((ally) => ({ value: ally.id, label: allyDisplayName(ally) })),
    [allies],
  );

  const allyNameById = useMemo(
    () => new Map(allies.map((ally) => [ally.id, allyDisplayName(ally)])),
    [allies],
  );

  const filtered = hasActiveFilters(filters);

  const columns = useMemo<ColumnsType<AdminLocationSummary>>(
    () => [
      {
        title: 'Ubicación',
        key: 'location',
        fixed: 'left',
        width: 260,
        render: (_, location) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{location.name}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}>
              {location.id}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: 'Dirección',
        key: 'address',
        width: 260,
        render: (_, location) => (
          <Space direction="vertical" size={0}>
            <span>{location.address}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {location.city}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: 'Aliado',
        key: 'ally',
        width: 220,
        render: (_, location) =>
          location.ally?.id ? allyNameById.get(location.ally.id) ?? location.ally.id : '—',
      },
      {
        // Informativa: el conteo de cargadores de la ubicación. Sin dato = 0 (el conteo real es 0).
        title: 'Cargadores',
        dataIndex: 'chargersCount',
        key: 'chargersCount',
        width: 120,
        align: 'right',
        render: (value?: number) => value ?? 0,
      },
      {
        title: 'Estado',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (status: LocationStatus) => <LocationStatusBadge status={status} />,
      },
      {
        title: 'Sincronización',
        dataIndex: 'syncStatus',
        key: 'syncStatus',
        width: 170,
        render: (_, location) => <LocationSyncBadge status={location.syncStatus} />,
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: 90,
        align: 'right',
        render: (_, location) => (
          <Button size="small" onClick={() => onSelect(location.id)}>
            Ver
          </Button>
        ),
      },
    ],
    [allyNameById, onSelect],
  );

  const clearFilters = () => {
    setQuery('');
    onFiltersChange({ status: 'ACTIVE' });
  };

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Buscar por nombre, municipio o dirección"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          style={{ width: 300 }}
          aria-label="Buscar ubicaciones"
        />
        <Select<string>
          showSearch
          allowClear
          optionFilterProp="label"
          value={filters.allyId}
          onChange={(value) => onFiltersChange({ ...filters, allyId: value })}
          placeholder="Todos los aliados"
          style={{ width: 240 }}
          aria-label="Filtrar por aliado"
          options={allyOptions}
        />
        <Select<LocationStatus | 'ALL'>
          value={filters.status ?? 'ALL'}
          onChange={(value) => onFiltersChange({ ...filters, status: value })}
          style={{ width: 190 }}
          aria-label="Filtrar por estado"
          options={STATUS_OPTIONS}
        />
      </Space>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar las ubicaciones"
          description={error}
          action={
            <Button size="small" onClick={onRetry}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Table<AdminLocationSummary>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={locations}
          scroll={{ x: 1360 }}
          rowClassName="locaciones-row"
          onRow={(location) => ({ onClick: () => onSelect(location.id) })}
          pagination={{
            current: Math.floor(meta.offset / Math.max(meta.limit, 1)) + 1,
            pageSize: meta.limit,
            total: meta.total,
            showSizeChanger: false,
            showTotal: (total) => `${total} ubicaciones`,
            onChange: (page) => onOffsetChange((page - 1) * meta.limit),
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={filtered ? 'Sin ubicaciones para los filtros' : 'Aún no hay ubicaciones'}
              >
                {filtered ? (
                  <Button onClick={clearFilters}>Limpiar filtros</Button>
                ) : (
                  canCreate && (
                    <Button type="primary" onClick={onCreate}>
                      Crear la primera ubicación
                    </Button>
                  )
                )}
              </Empty>
            ),
          }}
        />
      )}
    </div>
  );
}
